import React, { useState, useEffect } from 'react'
import { removeWhiteBackgroundFromUrl } from '@/utils/cleanLogoImage'

interface BrandLogoProps {
  src: string | null | undefined
  alt?: string
  style?: React.CSSProperties
  className?: string
  fallback?: React.ReactNode
  onError?: () => void
}

/**
 * BrandLogo — Universal zero-bleed logo renderer.
 *
 * Guarantees that no uploaded logo (round, square, or squircle) ever bleeds
 * a harsh white box or white corners onto dark or coloured backgrounds.
 * Automatically strips outer white backgrounds and corners in an offscreen canvas.
 */
export default function BrandLogo({
  src,
  alt = 'Logo',
  style,
  className,
  fallback = null,
  onError,
}: BrandLogoProps) {
  const [cleanSrc, setCleanSrc] = useState<string | null>(src || null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!src) {
      setCleanSrc(null)
      return
    }

    let active = true
    setFailed(false)
    setCleanSrc(src)

    removeWhiteBackgroundFromUrl(src)
      .then((processed) => {
        if (active && processed) {
          setCleanSrc(processed)
        }
      })
      .catch(() => {
        // keep using original src on non-fatal error
      })

    return () => {
      active = false
    }
  }, [src])

  if (!cleanSrc || failed) {
    return <>{fallback}</>
  }

  return (
    <img
      src={cleanSrc}
      alt={alt}
      className={className}
      onError={() => {
        setFailed(true)
        onError?.()
      }}
      style={{
        objectFit: 'contain',
        display: 'block',
        background: 'transparent',
        border: 'none',
        ...style,
      }}
    />
  )
}
